import { Inmate } from '@reliance/types/models/Inmate';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import { useAlerts } from '../../../alert/AlertContext';
import api from '../../../common/api';
import { useUser } from '../../../contexts/UserContext';
import { useFaceLoginAttemptsByInmateId } from '../../../hooks/FaceLogin';
import { useTabLazyLoad } from '../../../hooks/useTabLazyLoad';
import { useNotifyTabLoading } from '../../../hooks/useTabLoadingReporter';
import { useServerTableFilterState } from '../../../table/useServerTableFilterState';
import FaceLoginAttemptsTable, {
  FACE_LOGIN_ATTEMPT_SERVER_COLUMNS,
} from './FaceLoginAttemptsTable';

type InmateFaceLoginProps = {
  inmate: Inmate;
  tabSelected?: boolean;
  refreshTrigger?: number;
  onInmateUpdate?: () => void;
};

const InmateFaceLogin: React.FC<InmateFaceLoginProps> = (props) => {
  const { inmate, tabSelected, refreshTrigger, onInmateUpdate } = props;
  const alerts = useAlerts();
  const user = useUser();
  const [turningOffRecording, setTurningOffRecording] = useState(false);

  const baseRecord = useMemo(
    () => ({ inmateId: inmate.id, orderBy: '-dateReceived' }) as Record<string, unknown>,
    [inmate.id]
  );
  const tableState = useServerTableFilterState(FACE_LOGIN_ATTEMPT_SERVER_COLUMNS, baseRecord, {
    initialPagination: { pageIndex: 0, pageSize: 50 },
    initialSorting: [{ id: 'time', desc: true }],
  });

  const filters = useMemo(() => {
    const merged = tableState.mergedRecord;
    const reason = merged.reason as string | undefined;
    const passed = merged.passed as string | undefined;
    return {
      inmateId: inmate.id,
      page: merged.page as number,
      perPage: merged.perPage as number,
      orderBy: (merged.orderBy as string | undefined) ?? '-dateReceived',
      ...(reason != null && reason !== '' && { reason }),
      ...(passed != null && passed !== '' && { passed }),
    };
  }, [inmate.id, tableState.mergedRecord]);

  const attemptsResult = useFaceLoginAttemptsByInmateId(null, filters);

  useTabLazyLoad({
    key: inmate.id,
    tabSelected,
    refreshTrigger,
    execute: useCallback(() => {
      if (inmate.id) {
        void attemptsResult.execute(inmate.id);
      }
    }, [attemptsResult, inmate.id]),
  });

  useEffect(() => {
    if (tabSelected && inmate.id) {
      void attemptsResult.execute(inmate.id);
    }
  }, [tabSelected, inmate.id, tableState.mergedRecord]);

  useNotifyTabLoading('face-login', attemptsResult.loading);

  const canTurnOffRecording = Boolean(user?.hasPermission?.('inmate_manage'));

  const handleTurnOffRecording = useCallback(async () => {
    if (!inmate?.account || !inmate.facility?.id) {
      return;
    }
    setTurningOffRecording(true);
    try {
      await api.put(`/inmate/${inmate.account}?facilityId=${inmate.facility.id}`, {
        recordFaceLoginVideo: false,
      });
      alerts.success('Face login recording was turned off');
      onInmateUpdate?.();
    } catch {
      alerts.error('An error occurred while turning off face login recording', true);
    } finally {
      setTurningOffRecording(false);
    }
  }, [alerts, inmate, onInmateUpdate]);

  const attempts = attemptsResult.result?.data ?? [];
  const total = attemptsResult.result?.total ?? 0;
  const perPage = attemptsResult.result?.perPage ?? tableState.pagination.pageSize;
  const totalPages = Math.ceil(total / perPage) || 1;
  const page = attemptsResult.result?.page ?? tableState.pagination.pageIndex + 1;

  return (
    <div className="jarviswidget">
      <div className="row">
        <div className="col-md-12 mb-3">
          {inmate.recordFaceLoginVideo && (
            <Alert variant="warning" className="d-flex align-items-center justify-content-between">
              <div>
                Face login recording is on for this inmate.
                Turn it off after you finish reviewing samples.
              </div>
              {canTurnOffRecording && (
                <Button
                  variant="warning"
                  className="ms-3 flex-shrink-0"
                  disabled={turningOffRecording}
                  onClick={handleTurnOffRecording}>
                  {turningOffRecording ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-1"
                        role="status"
                        aria-hidden="true"
                      />
                      Turning off...
                    </>
                  ) : (
                    'Turn off recording'
                  )}
                </Button>
              )}
            </Alert>
          )}

          <Card>
            <Card.Header>
              <h5 className="mb-0">Attempts</h5>
            </Card.Header>
            <Card.Body>
              <FaceLoginAttemptsTable
                attempts={attempts}
                loading={attemptsResult.loading}
                paginationMode="server"
                page={page}
                totalPages={totalPages}
                total={total}
                perPage={perPage}
                serverPagination={tableState.serverPagination}
                onServerPaginationChange={tableState.onServerPaginationChange}
                serverSorting={tableState.serverSorting}
                onServerSortingChange={tableState.onServerSortingChange}
                serverColumnFilters={tableState.serverColumnFilters}
                onServerColumnFiltersChange={tableState.onServerColumnFiltersChange}
              />
            </Card.Body>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default InmateFaceLogin;
